import { Button, Divider, Table } from "antd";
import React, { useEffect, useState } from "react";
import ManageBinService from "../../../../../services/Dispatch-Centre-Service/ManageBinService";
import ResponseMessage from "../../../../Comman/Notification/ResponseMessge";
import { useNavigate } from "react-router";

const tableStyles = `
  .compact-table .ant-table-thead > tr > th {
    padding: 8px 4px;
  }

  .compact-table .ant-table-tbody > tr > td {
    padding: 6px 4px;
  }

  .compact-table .ant-table-row {
    height: 32px;
  }

  .compact-table .color-column {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .confirm-btn-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
    padding-bottom: 8px;
  }

  .confirm-btn {
    min-width: 220px;
    height: 44px;
    border-radius: 10px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .confirm-btn {
      width: 100%;
      max-width: 280px;
    }
  }
`;

const BulkOutwardPreview = ({ selectedBatch }) => {
  const [binData, setBinData] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBinData = async () => {
      try {
        const storedBinIds =
          JSON.parse(localStorage.getItem("bin_bulk_outward")) || [];
        const binIdsArray = storedBinIds.map((bin) => bin.bin_id);

        if (!binIdsArray.length) {
          ResponseMessage.error({
            message: "No bin IDs found in local storage.",
          });
          return;
        }

        const response = await ManageBinService.getBulkBinData(binIdsArray);

        if (response?.status === true && response?.data) {
          setBinData(processBinData(response.data));
        } else {
          ResponseMessage.error({
            message: "No data received or invalid response.",
          });
        }
      } catch (error) {
        ResponseMessage.error({
          message: `Failed to fetch bin data: ${error.message}`,
        });
      }
    };

    fetchBinData();
  }, []);

  const extractColorName = (color) => {
    if (!color) return "Unknown Color";

    if (color.startsWith("00:")) {
      color = color.replace("00:", "");
    }

    const words = color.split(" ");
    let firstSpecIndex = -1;

    for (let i = 0; i < words.length; i++) {
      if (words[i].includes("/") && /\d/.test(words[i])) {
        firstSpecIndex = i;
        break;
      }
    }

    return firstSpecIndex >= 0
      ? words.slice(0, firstSpecIndex).join(" ").trim()
      : color.trim();
  };

  const processBinData = (data) => {
    const itemMap = new Map();

    data.forEach((bin) => {
      bin.bin_data.forEach(
        ({ item_no, color, kanban_no, size_qty, total_qty }) => {
          const validItemNo = item_no || "Unknown Item";
          const cleanedColor = extractColorName(color);
          const sizesParsed = JSON.parse(size_qty);

          if (!itemMap.has(validItemNo)) {
            itemMap.set(validItemNo, new Map());
          }

          const colorMap = itemMap.get(validItemNo);

          if (!colorMap.has(cleanedColor)) {
            colorMap.set(cleanedColor, {
              sizeQty: {},
              total: 0,
              kanban_nos: [],
            });
          }

          const colorData = colorMap.get(cleanedColor);
          colorData.kanban_nos.push(kanban_no);

          Object.entries(sizesParsed).forEach(([size, qty]) => {
            const numericQty = Number(qty);
            colorData.sizeQty[size] =
              (Number(colorData.sizeQty[size]) || 0) + numericQty;
          });

          colorData.total = Number(colorData.total) + Number(total_qty);
        }
      );
    });

    const result = Array.from(itemMap, ([item_no, colorMap]) => {
      const availableSizes = new Set();

      const details = Array.from(
        colorMap,
        ([color, { sizeQty, total, kanban_nos }]) => {
          Object.keys(sizeQty).forEach((size) => availableSizes.add(size));

          return {
            color,
            sizeQty,
            total,
            kanban_no: kanban_nos.join(", "),
          };
        }
      );

      return {
        item_no,
        details,
        availableSizes: Array.from(availableSizes),
      };
    });

    return result;
  };

  const handleConfirmBinOutward = async () => {
    if (!selectedBatch) {
      ResponseMessage.error({
        message: "Please select a batch before confirming.",
      });
      return;
    }

    const userData = JSON.parse(localStorage.getItem("userData"));
    const addedBy = userData?.data?.su_id;
    const storedBinIds =
      JSON.parse(localStorage.getItem("bin_bulk_outward")) || [];
    const binIdsArray = storedBinIds.map((bin) => bin.bin_id);

    try {
      setConfirmLoading(true);

      const response = await ManageBinService.BulkOutward(
        binIdsArray,
        addedBy
      );

      ResponseMessage.success({ message: response.message });

      const existingSummary = JSON.parse(localStorage.getItem("summary_data")) || {
        selectedBatch: null,
        records: [],
      };

      const newRecord = {
        selectedBatch,
        binData,
        confirmedAt: new Date().toISOString(),
      };

      const updatedRecords = [...existingSummary.records, newRecord];

      const updatedSummaryData = {
        selectedBatch: selectedBatch || existingSummary.selectedBatch,
        records: updatedRecords,
      };

      localStorage.setItem("summary_data", JSON.stringify(updatedSummaryData));
      localStorage.removeItem("bin_bulk_outward");

      navigate(-1);
    } catch (error) {
      ResponseMessage.error({ message: "Failed to confirm bin outward." });
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
      <style>{tableStyles}</style>

      {binData.map(({ item_no, details, availableSizes }) => {
        const columns = [
          {
            title: "Color",
            dataIndex: "color",
            key: "color",
            width: 100,
            className: "color-column",
            ellipsis: true,
          },
          ...availableSizes.map((size) => ({
            title: size,
            dataIndex: ["sizeQty", size],
            key: size,
            width: 60,
            align: "center",
            render: (text) => text || "-",
          })),
          {
            title: "Total",
            dataIndex: "total",
            key: "total",
            width: 60,
            align: "center",
          },
        ];

        return (
          <div key={item_no} style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Item: {item_no}</h3>
            <Table
              columns={columns}
              dataSource={details}
              pagination={false}
              bordered
              rowKey="color"
              size="small"
              className="compact-table"
              scroll={{ x: "max-content" }}
            />
          </div>
        );
      })}

      <Divider />

      <div className="confirm-btn-wrap">
        <Button
          type="primary"
          danger
          size="large"
          className="confirm-btn"
          loading={confirmLoading}
          disabled={!selectedBatch}
          onClick={handleConfirmBinOutward}
        >
          Confirm Outward
        </Button>
      </div>
    </>
  );
};

export default BulkOutwardPreview;